import { useCallback, useMemo, useState } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';

import { SearchLoupeQuestion } from '@assets/icons/SearchLoupeQuestion';
import { Button } from '@components/Button';
import { JobsBoardPostsPublicResult } from '@hooks/jobsBoard/useGetJobsBoardPostsPublic/useGetJobsBoardPostsPublic';
import {
  useModalVisibility,
  UseModalVisibilityHookReturn,
} from '@hooks/shared/useModalVisibility';
import { useJobsBoardFiltersViewContext } from '@views/jobsBoard/context/JobsBoardFiltersViewContext';

import { JobsBoardPost } from './JobsBoardPost/JobsBoardPost';
import { JobsBoardPostDrawer } from './JobsBoardPostDrawer';

type JobsBoardPostsProps = {
  publicJobsBoardPostsInfo: JobsBoardPostsPublicResult;
  jobsFiltersModalVisibility: UseModalVisibilityHookReturn;
};

export const JobsBoardPostsList = ({
  publicJobsBoardPostsInfo,
  jobsFiltersModalVisibility,
}: JobsBoardPostsProps) => {
  const {
    isVisible: isDrawerOpen,
    showModal: showDrawer,
    hideModal: closeDrawer,
  } = useModalVisibility();

  const { filtersCount, onClearFilters } = useJobsBoardFiltersViewContext();
  const [selectedJobPostApplicationId, setSelectedJobPostApplicationId] =
    useState<number | undefined>(null);

  const selectedApplication = useMemo(() => {
    if (!selectedJobPostApplicationId) {
      return null;
    }
    return (
      publicJobsBoardPostsInfo?.data?.find(
        (post) => post?.id === selectedJobPostApplicationId
      ) ?? null
    );
  }, [selectedJobPostApplicationId, publicJobsBoardPostsInfo?.data]);

  const onOpenDrawer = useCallback(
    (applicationId: number) => {
      setSelectedJobPostApplicationId(applicationId);
      showDrawer();
    },
    [showDrawer]
  );

  if (publicJobsBoardPostsInfo?.isLoading) {
    return (
      <StyledSpinContainer>
        <Spin />
      </StyledSpinContainer>
    );
  }

  if (publicJobsBoardPostsInfo?.isError || !publicJobsBoardPostsInfo.data) {
    return <div>Error Loading Jobs Board Posts</div>;
  }

  if (!publicJobsBoardPostsInfo?.data?.length) {
    return (
      <NoResultsDisplay
        filtersCount={filtersCount}
        jobsFiltersModalVisibility={jobsFiltersModalVisibility}
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <StyledPostsContainer>
      <JobsBoardPostDrawer
        jobPost={selectedApplication}
        open={isDrawerOpen}
        onClose={closeDrawer}
        closeDrawer={closeDrawer}
        destroyOnClose
        afterOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelectedJobPostApplicationId(null);
          }
        }}
      />
      {publicJobsBoardPostsInfo.data.map((post) => (
        <JobsBoardPost
          key={post.id}
          post={post}
          onOpenDrawer={onOpenDrawer}
          selectedJobPostApplicationId={selectedJobPostApplicationId}
          isDrawerOpen={isDrawerOpen}
        />
      ))}
    </StyledPostsContainer>
  );
};

type NoResultsDisplayProps = {
  filtersCount: number;
  jobsFiltersModalVisibility: UseModalVisibilityHookReturn;
  onClearFilters: () => void;
};

const NoResultsDisplay = ({
  filtersCount,
  jobsFiltersModalVisibility,
  onClearFilters,
}: NoResultsDisplayProps) => {
  return (
    <StyledNoResultsContainer>
      <div className='no-results-header'>
        <div className='icon-container-section'>
          <div className='icon-container'>
            <SearchLoupeQuestion />
          </div>
        </div>
        <div className='no-results-title'>No listings match your filters</div>
      </div>
      {!!filtersCount && (
        <div className='no-results-body'>
          <div className='no-results-subtitle'>
            Try adjusting your filters or clearing them to see all listings.
          </div>
          <div className='no-results-button-container'>
            <Button
              type='primary'
              onClick={jobsFiltersModalVisibility.showModal}
            >
              Edit Filters
            </Button>
            <Button onClick={onClearFilters}>Clear Filters</Button>
          </div>
        </div>
      )}
    </StyledNoResultsContainer>
  );
};

const StyledNoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px 0 0;
  gap: 4px;
  width: 400px;
  margin: 0 auto;
  text-align: center;

  font-size: 14px;
  line-height: 20px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 72px 0 0;

    font-size: 16px;
    line-height: 24px;
  }

  .no-results-header {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .icon-container-section {
      display: flex;
      align-items: center;
      justify-content: center;

      .icon-container {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .no-results-title {
      color: ${({ theme }) => theme.palette.text[9]};
      font-weight: 600;
    }
  }

  .no-results-body {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .no-results-subtitle {
      color: ${({ theme }) => theme.palette.text[9]};
    }

    .no-results-button-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .ant-btn {
        width: 150px;
        border-width: 1px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 9px 0;
        font-weight: 600;
        line-height: 20px;
        font-size: 14px;
        height: 40px;

        &.ant-btn-default {
          background: #fff;

          &:hover {
            background: ${({ theme }) => theme.palette.greyscale[1]};
          }
        }

        @media (max-width: 768px) {
          width: 100%;
        }
      }
    }
  }
`;

const StyledSpinContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0 0;
`;

const StyledPostsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
